import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Bucket, RunDetail, RunStep } from '../../services/executionStatsService';
import { useBucketColours, BUCKET_LABELS, count, duration } from './executionStatsVocabulary';
import { groupErrors, when } from './runPresentation';
import { dsVar, ink } from '../../theme/tokens';

/** What became of a configured node in this run. */
export type NodeState =
    | 'ran'
    /** Not run, in a run that finished: a branch that wasn't taken. */
    | 'skipped'
    /** Not run, after the run failed. */
    | 'notReached'
    /** The first node not yet logged in a run still going (or stuck). */
    | 'frontier'
    /** Not run yet, beyond the frontier. */
    | 'pending';

export interface TimelineEntry {
    key: string;
    name: string;
    code: string | null;
    /** Every execution of this node, oldest first. A fan-out runs a node many times. */
    runs: RunStep[];
    /** The worst outcome among its runs; null before it has run. */
    bucket: Bucket | null;
    state: NodeState;
    /** Logged, but not a node of the flow as configured (a sub-flow's, say). */
    unconfigured?: boolean;
}

type Node = RunDetail['configuration']['nodes'][number];
type Edge = RunDetail['configuration']['edges'][number];

/** Worst first: the bucket a node shows when its runs disagree. */
const SEVERITY: Bucket[] = ['failed', 'stalled', 'pendingReview', 'running', 'success'];

const worst = (runs: RunStep[]): Bucket | null => {
    const buckets = new Set(runs.map((run) => run.bucket));
    return SEVERITY.find((bucket) => buckets.has(bucket)) ?? null;
};

/**
 * Configured nodes in execution order: each after everything that feeds it,
 * ties kept in configuration order. A cycle can't be ordered, so whatever is
 * left over follows in configuration order rather than being dropped.
 */
export const orderNodes = (nodes: Node[], edges: Edge[]): Node[] => {
    const ids = new Set(nodes.map((node) => node.id).filter(Boolean) as string[]);
    const incoming = new Map<string, number>();
    const next = new Map<string, string[]>();
    nodes.forEach((node) => node.id && incoming.set(node.id, 0));
    edges.forEach(({ source, target }) => {
        if (!source || !target || !ids.has(source) || !ids.has(target)) return;
        incoming.set(target, (incoming.get(target) ?? 0) + 1);
        next.set(source, [...(next.get(source) ?? []), target]);
    });

    const ordered: Node[] = [];
    const placed = new Set<Node>();
    let ready = nodes.filter((node) => !node.id || incoming.get(node.id) === 0);
    while (ready.length) {
        const [node, ...rest] = ready;
        ready = rest;
        if (placed.has(node)) continue;
        placed.add(node);
        ordered.push(node);
        const unblocked: string[] = [];
        for (const target of node.id ? next.get(node.id) ?? [] : []) {
            const remaining = (incoming.get(target) ?? 0) - 1;
            incoming.set(target, remaining);
            if (remaining === 0) unblocked.push(target);
        }
        // Keep configuration order among nodes that become ready together.
        ready = [...ready, ...nodes.filter((n) => n.id !== null && unblocked.includes(n.id))]
            .sort((a, b) => nodes.indexOf(a) - nodes.indexOf(b));
    }
    return [...ordered, ...nodes.filter((node) => !placed.has(node))];
};

/**
 * The flow as configured, each node with what it did in this run.
 *
 * Steps are matched to nodes by template code. Node rows are only written when
 * a node returns, so in a run still going the first unlogged node is where it
 * is now — or where it stopped, for a stalled run.
 */
export const buildTimeline = (detail: RunDetail): TimelineEntry[] => {
    const { nodes, edges } = detail.configuration;
    const steps = detail.steps ?? [];
    const runBucket = detail.run.bucket;

    const byCode = new Map<string, RunStep[]>();
    steps.forEach((step) => {
        const code = step.code ?? '';
        byCode.set(code, [...(byCode.get(code) ?? []), step]);
    });

    const ordered = orderNodes(nodes, edges);
    const configuredCodes = new Set(nodes.map((node) => node.code ?? ''));
    const ranIds = new Set(
        ordered.filter((node) => (byCode.get(node.code ?? '') ?? []).length > 0).map((node) => node.id)
    );
    const parents = new Map<string, string[]>();
    edges.forEach(({ source, target }) => {
        if (source && target) parents.set(target, [...(parents.get(target) ?? []), source]);
    });

    const unfinished = runBucket === 'running' || runBucket === 'stalled' || runBucket === 'pendingReview';
    const notRunState = (node: Node): NodeState => {
        if (runBucket === 'success') return 'skipped';
        if (!unfinished) return 'notReached';
        const feeds = node.id ? parents.get(node.id) ?? [] : [];
        return feeds.every((id) => ranIds.has(id)) ? 'frontier' : 'pending';
    };

    const entries: TimelineEntry[] = ordered.map((node, index) => {
        const runs = byCode.get(node.code ?? '') ?? [];
        return {
            key: node.id ?? `node-${index}`,
            name: node.name ?? node.code ?? 'Unnamed step',
            code: node.code,
            runs,
            bucket: worst(runs),
            state: runs.length ? 'ran' : notRunState(node)
        };
    });

    // Steps the configuration doesn't name, in the order they first ran.
    [...byCode.entries()]
        .filter(([code]) => !configuredCodes.has(code))
        .forEach(([code, runs]) => entries.push({
            key: `logged-${code}`,
            name: runs[0].name ?? (code || 'Unnamed step'),
            code: code || null,
            runs,
            bucket: worst(runs),
            state: 'ran',
            unconfigured: true
        }));

    return entries;
};

const STATE_TEXT: Record<Exclude<NodeState, 'ran'>, string> = {
    skipped: 'Skipped — a branch this run didn\'t take',
    notReached: 'Not reached',
    frontier: 'In progress, or next to run',
    pending: 'Not reached yet'
};

/** A node that hasn't run, in words: why not, or what it's doing. */
const notRunText = (state: Exclude<NodeState, 'ran'>, runBucket: Bucket | null): string => {
    if (state !== 'frontier') return STATE_TEXT[state];
    if (runBucket === 'stalled') return 'Likely stopped here — no result was recorded';
    if (runBucket === 'pendingReview') return 'Waiting here for a review';
    return STATE_TEXT.frontier;
};

const summary = (entry: TimelineEntry): string => {
    if (entry.runs.length === 1) {
        const [run] = entry.runs;
        if (run.durationMs !== null) return `Took ${duration(run.durationMs)}`;
        return run.completedAt ? `Finished ${when(run.completedAt)}` : 'No finish recorded';
    }
    const failed = entry.runs.filter((run) => run.bucket === 'failed').length;
    const longest = Math.max(...entry.runs.map((run) => run.durationMs ?? 0));
    return [
        `Ran ${count(entry.runs.length)} times`,
        failed ? `${count(failed)} failed` : null,
        longest ? `longest ${duration(longest)}` : null
    ].filter(Boolean).join(' · ');
};

const EntryError: React.FC<{ entry: TimelineEntry }> = ({ entry }) => {
    const [open, setOpen] = useState(false);
    const groups = groupErrors(entry.runs);
    if (!groups.length) return null;
    const [top] = groups;
    const full = entry.runs.find((run) => run.errorMessage)?.errorMessage ?? top.example;
    return (
        <Typography
            component="button"
            onClick={() => setOpen((o) => !o)}
            title={open ? 'Collapse' : 'Show the whole error'}
            sx={{
                display: 'block', textAlign: 'left', mt: 0.5, p: 0, border: 'none', background: 'none', cursor: 'pointer',
                fontSize: '0.6875rem', color: ink('#FCA5A5'), fontFamily: '"JetBrains Mono", monospace', maxWidth: '100%',
                whiteSpace: open ? 'pre-wrap' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word'
            }}
        >
            {groups.length > 1 || top.runs > 1 ? `${count(top.runs)} × ` : ''}
            {open ? full : top.example}
            {groups.length > 1 ? ` (+${count(groups.length - 1)} other ${groups.length === 2 ? 'error' : 'errors'})` : ''}
        </Typography>
    );
};

/**
 * The run step by step: the flow as configured, each node with its outcome,
 * and where a run that hasn't finished stands.
 *
 * Styled with sx: it renders inside a portalled drawer.
 */
const RunStepTimeline: React.FC<{ detail: RunDetail }> = ({ detail }) => {
    const bucketColours = useBucketColours();
    if (detail.steps === null) {
        return (
            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted'), fontStyle: 'italic' }}>
                Step-by-step detail isn't available in this environment yet.
            </Typography>
        );
    }

    const entries = buildTimeline(detail);
    if (!entries.length) {
        return (
            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted'), fontStyle: 'italic' }}>
                No steps were recorded for this run.
            </Typography>
        );
    }

    return (
        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {entries.map((entry, index) => {
                const ran = entry.state === 'ran';
                const dot = entry.bucket ? bucketColours[entry.bucket] : entry.state === 'frontier' ? dsVar('accent-fg') : '#334155';
                const last = index === entries.length - 1;
                return (
                    <Box component="li" key={entry.key} sx={{ display: 'flex', gap: 1.25 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
                            <Box
                                sx={{
                                    width: 10, height: 10, borderRadius: '50%', flex: 'none',
                                    backgroundColor: ran || entry.state === 'frontier' ? dot : 'transparent',
                                    border: `2px solid ${dot}`
                                }}
                            />
                            {!last && <Box sx={{ width: '1px', flex: 1, backgroundColor: dsVar('line'), my: 0.5 }} />}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1, pb: last ? 0 : 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                                <Typography
                                    sx={{ fontSize: '0.8125rem', fontWeight: 600, color: ran ? dsVar('fg-body') : dsVar('fg-muted'), minWidth: 0 }}
                                    noWrap
                                    title={entry.name}
                                >
                                    {entry.name}
                                </Typography>
                                {entry.bucket && (
                                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-soft'), flex: 'none' }}>
                                        {BUCKET_LABELS[entry.bucket]}
                                    </Typography>
                                )}
                            </Box>
                            {entry.code && (
                                <Typography sx={{ fontSize: '0.625rem', color: dsVar('fg-faint'), fontFamily: '"JetBrains Mono", monospace' }} noWrap title={entry.code}>
                                    {entry.code}{entry.unconfigured ? ' · not in this workflow\'s configuration' : ''}
                                </Typography>
                            )}
                            <Typography
                                sx={{
                                    fontSize: '0.6875rem',
                                    color: entry.state === 'frontier' ? dsVar('accent-fg') : dsVar('fg-muted'),
                                    fontStyle: ran ? 'normal' : 'italic'
                                }}
                            >
                                {entry.state === 'ran' ? summary(entry) : notRunText(entry.state, detail.run.bucket)}
                            </Typography>
                            <EntryError entry={entry} />
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default RunStepTimeline;
